import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, CheckCircle2, Loader2 } from 'lucide-react';
import { partnerIntakeService } from '../services/partnerIntakeService';
import { scrollToTop } from '../utils/scrollUtils';

const DriverRegistrationPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Become a Driver - AbleGo';
    scrollToTop('instant');
  }, []);

  const [full_name, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [town_or_region, setTownOrRegion] = useState('');
  const [date_of_birth, setDateOfBirth] = useState('');
  const [driving_license_number, setDrivingLicenseNumber] = useState('');
  const [vehicle_make, setVehicleMake] = useState('');
  const [vehicle_model, setVehicleModel] = useState('');
  const [vehicle_year, setVehicleYear] = useState(new Date().getFullYear());
  const [registration_plate, setRegistrationPlate] = useState('');
  const [wheelchair_accessible, setWheelchairAccessible] = useState(false);
  const [passenger_seats, setPassengerSeats] = useState(4);
  const [agrees_video_validation, setAgreesVideoValidation] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await partnerIntakeService.submitDriver({
        full_name,
        email,
        phone,
        town_or_region,
        date_of_birth,
        driving_license_number,
        vehicle_make,
        vehicle_model,
        vehicle_year,
        registration_plate,
        wheelchair_accessible,
        passenger_seats,
        agrees_video_validation,
      });
      if (!result.success) {
        setError(result.error || 'Something went wrong. Please try again.');
        return;
      }
      setReference(result.reference || null);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 dark:from-dark-900 dark:to-dark-800 pt-20 pb-16">
        <div className="container mx-auto px-4 max-w-lg">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-3">
              Application received
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-2">
              Thank you. We will email you shortly. Your reference is{' '}
              <strong className="text-gray-900 dark:text-gray-100">{reference}</strong>.
            </p>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6">
              <strong>Next step:</strong> our team will contact you to arrange a short video call to
              confirm your details and explain onboarding. No account is created until after that
              conversation.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 dark:from-dark-900 dark:to-dark-800 pt-20 pb-16">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 text-white shadow-lg mb-4">
            <Car className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50 tracking-tight">
            Drive with AbleGo
          </h1>
          <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm max-w-md mx-auto leading-relaxed">
            One short form — we only ask for what we need to start. We will validate everything in a
            video call before any account or vehicle goes live.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8 space-y-8"
        >
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Your details
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="sm:col-span-2 block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Full name</span>
                <input
                  required
                  value={full_name}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Phone</span>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Town or region you cover
                </span>
                <input
                  required
                  placeholder="e.g. Bristol, Gloucestershire"
                  value={town_or_region}
                  onChange={(e) => setTownOrRegion(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Date of birth
                </span>
                <input
                  type="date"
                  required
                  value={date_of_birth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Driving licence number
                </span>
                <input
                  required
                  autoComplete="off"
                  value={driving_license_number}
                  onChange={(e) => setDrivingLicenseNumber(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Vehicle
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Make</span>
                <input
                  required
                  value={vehicle_make}
                  onChange={(e) => setVehicleMake(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Model</span>
                <input
                  required
                  value={vehicle_model}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Year</span>
                <input
                  type="number"
                  required
                  min={1995}
                  max={new Date().getFullYear() + 1}
                  value={vehicle_year}
                  onChange={(e) => setVehicleYear(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Registration
                </span>
                <input
                  required
                  value={registration_plate}
                  onChange={(e) => setRegistrationPlate(e.target.value.toUpperCase())}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm uppercase"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Passenger seats
                </span>
                <select
                  value={passenger_seats}
                  onChange={(e) => setPassengerSeats(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-gray-200 dark:border-dark-600 dark:bg-dark-900 px-3 py-2.5 text-sm"
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center sm:col-span-2">
                <input
                  id="wc"
                  type="checkbox"
                  checked={wheelchair_accessible}
                  onChange={(e) => setWheelchairAccessible(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="wc" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  This vehicle can carry a wheelchair passenger (accessible / WAV)
                </label>
              </div>
            </div>
          </section>

          <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/40 px-4 py-3">
            <p className="text-sm text-amber-950 dark:text-amber-100 leading-relaxed">
              <strong>Video validation:</strong> after you submit, we schedule a brief video call to
              check your submission and credentials. Completing this form does not create a login —
              we will onboard you properly after verification.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agrees_video_validation}
              onChange={(e) => setAgreesVideoValidation(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 leading-snug">
              I understand AbleGo will contact me to arrange a video call before my application is
              finalised.
            </span>
          </label>

          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !agrees_video_validation}
            className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending…
              </>
            ) : (
              'Submit application'
            )}
          </button>

          <p className="text-center text-xs text-gray-500">
            Already registered?{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default DriverRegistrationPage;
